/**
 * ApiError - unified frontend error class.
 * Replaces raw axios error objects and plain { success:false, message }.
 * Usage: throw new ApiError({ code, message, statusCode, details, requestId })
 */

export class ApiError extends Error {
    constructor({ code, message, statusCode, details, requestId, path, timestamp, cause } = {}) {
        super(message || 'Unexpected error');
        this.name = 'ApiError';
        this.code = code || 'UNKNOWN';
        this.statusCode = statusCode ?? 0;
        this.details = details;
        this.requestId = requestId;
        this.path = path;
        this.timestamp = timestamp;
        // Keep original axios error for debugging (not shown to user)
        this.cause = cause;
        if (Error.captureStackTrace) Error.captureStackTrace(this, ApiError);
    }

    /** True if auth is required / session expired – code-based, status is fallback only */
    isAuthError() {
        if (['AUTH_REQUIRED', 'UNAUTHORIZED', 'INVALID_TOKEN', 'TOKEN_EXPIRED', 'SESSION_EXPIRED'].includes(this.code)) return true;
        // Fallback only when backend omitted code (legacy) – status alone is not authoritative
        if (!this.code || this.code === 'UNKNOWN') return this.statusCode === 401;
        return false;
    }

    isForbidden() {
        if (['FORBIDDEN', 'INSUFFICIENT_PERMISSION', 'ROLE_REQUIRED', 'STEP_UP_REQUIRED'].includes(this.code)) return true;
        if (!this.code || this.code === 'UNKNOWN') return this.statusCode === 403;
        return false;
    }

    isValidationError() {
        if (this.code === 'VALIDATION_FAILED') return true;
        if (!this.code || this.code === 'UNKNOWN') return this.statusCode === 400;
        return false;
    }

    isNotFound() {
        return this.code === 'RESOURCE_NOT_FOUND' || this.code === 'NOT_FOUND' || this.statusCode === 404;
    }

    isRateLimited() {
        if (['RATE_LIMITED', 'TOO_MANY_REQUESTS', 'OTP_RATE_LIMITED'].includes(this.code)) return true;
        if (!this.code || this.code === 'UNKNOWN') return this.statusCode === 429;
        return this.statusCode === 429;
    }

    isStepUpRequired() {
        return this.code === 'STEP_UP_REQUIRED';
    }

    isRoleRequired() {
        return this.code === 'ROLE_REQUIRED';
    }

    isServerError() {
        return this.statusCode >= 500;
    }

    isNetworkError() {
        return this.statusCode === 0;
    }

    toJSON() {
        return {
            name: this.name,
            message: this.message,
            code: this.code,
            statusCode: this.statusCode,
            details: this.details,
            requestId: this.requestId,
            path: this.path,
        };
    }
}

/**
 * Parse any axios/fetch error into ApiError.
 * Handles:
 * - error.response.data (ApiErrorResponse from backend)
 * - network errors (no response)
 * - timeout / offline
 */
export function parseApiError(error) {
    if (error instanceof ApiError) return error;

    // Already normalized QueryError from queryError.js
    if (error && error.isQueryError) {
        // Convert QueryError to ApiError for uniform handling
        const data = error.data || {};
        return new ApiError({
            code: data.code || mapStatusToCode(error.status),
            message: data.message || error.message || 'Unexpected error',
            statusCode: error.status ?? 0,
            details: data.details || data.errors,
            requestId: data.requestId,
            path: data.path,
            cause: error,
        });
    }

    if (error && error.response) {
        const { status, data } = error.response;
        // Backend unified shape: { success:false, code, message, statusCode, details, requestId, timestamp, path }
        const code = data?.code ? String(data.code) : mapStatusToCode(status);
        const message = data?.message || data?.error || error.message || 'Unexpected error';
        // message may be array (validation); normalize to first string for top-level
        const normalizedMessage = Array.isArray(message) ? message.join(', ') : String(message);
        return new ApiError({
            code,
            message: normalizedMessage,
            statusCode: data?.statusCode ?? status,
            details: data?.details || data?.errors,
            requestId: data?.requestId,
            path: data?.path,
            timestamp: data?.timestamp,
            cause: error,
        });
    }

    if (error && error.request) {
        // No response = network / offline / timeout
        const isTimeout = error.code === 'ECONNABORTED' || String(error.message).toLowerCase().includes('timeout');
        return new ApiError({
            code: isTimeout ? 'NETWORK_TIMEOUT' : 'NETWORK_ERROR',
            message: isTimeout ? 'درخواست زمان زیادی طول کشید. لطفا دوباره تلاش کنید' : 'خطا در برقراری ارتباط با سرور',
            statusCode: 0,
            cause: error,
        });
    }

    // Fallback
    return new ApiError({
        code: 'UNKNOWN',
        message: error?.message || 'خطای نامشخص',
        statusCode: 0,
        cause: error,
    });
}

function mapStatusToCode(status) {
    if (!status) return 'NETWORK_ERROR';
    if (status === 400) return 'VALIDATION_FAILED';
    if (status === 401) return 'AUTH_REQUIRED';
    if (status === 403) return 'FORBIDDEN';
    if (status === 404) return 'RESOURCE_NOT_FOUND';
    if (status === 409) return 'CONFLICT';
    if (status === 429) return 'RATE_LIMITED';
    if (status >= 500) return 'INTERNAL_ERROR';
    return 'UNKNOWN';
}

/**
 * Type guard
 */
export function isApiError(error) {
    return error instanceof ApiError;
}
